import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import Button from '../Common/Button';
import AccountSettings from './AccountSettings';
import VoiceSettings from './VoiceSettings';
import AppSettings from './AppSettings';
import LLMProviderSettings from './LLMProviderSettings';

const SettingsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dark.bg};
  overflow: hidden;
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.primary[400]};
    background: ${({ theme }) => theme.colors.primary[500]}10;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SettingsTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SettingsContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  min-height: 0;
`;

const SettingsNav = styled.nav`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  height: fit-content;
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const NavItem = styled(motion.li)`
  margin: 0;
`;

const NavButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ isActive, theme }) => 
    isActive ? theme.colors.primary[500] : 'transparent'
  };
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ isActive, theme }) => 
    isActive ? 'white' : theme.colors.dark.textSecondary
  };
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;
  
  &:hover {
    background: ${({ isActive, theme }) => 
      isActive ? theme.colors.primary[600] : theme.colors.dark.elevated
    };
    color: ${({ isActive, theme }) => 
      isActive ? 'white' : theme.colors.dark.text
    };
  }
`;

const SettingsPanel = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  overflow-y: auto;
`;

const Settings = ({ onBack, navigationState, initialTab = 'account' }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('account');
  const [hasChanges, setHasChanges] = useState(false);
  const [highlightVoice, setHighlightVoice] = useState(false);
  const [highlightBackgroundSettings, setHighlightBackgroundSettings] = useState(false);
  const [highlightActiveHours, setHighlightActiveHours] = useState(false);

  useEffect(() => {
    // Check navigation state from component-based navigation
    if (navigationState) {
      // Set active tab if specified (only for initial load, allow user navigation after)
      if (navigationState.activeTab && !navigationState.allowTabNavigation) {
        setActiveTab(navigationState.activeTab);
      } else if (navigationState.activeTab && navigationState.allowTabNavigation) {
        // For dashboard navigation, set initial tab but allow subsequent navigation
        setActiveTab(navigationState.activeTab);
      }
      
      if (navigationState.highlightVoice) {
        setActiveTab('app'); // Set to App Settings tab (has Background Listening & Active Hours)
        
        // Don't highlight voice settings, instead highlight app settings
        setHighlightVoice(false);
      }
      
      if (navigationState.highlightBackgroundSettings) {
        setHighlightBackgroundSettings(true);
        
        // Clear highlight after 3 seconds and clear navigation state
        setTimeout(() => {
          setHighlightBackgroundSettings(false);
        }, 3000);
      }
      
      if (navigationState.highlightActiveHours) {
        setHighlightActiveHours(true);
        
        // Clear highlight after 3 seconds and clear navigation state
        setTimeout(() => {
          setHighlightActiveHours(false);
        }, 3000);
      }
    } else {
      // If no navigationState is provided, use initialTab or default to Account Settings
      // This handles the case when navigating from profile button or dashboard settings
      setActiveTab(initialTab);
      
      // Clear any existing highlights
      setHighlightVoice(false);
      setHighlightBackgroundSettings(false);
      setHighlightActiveHours(false);
    }
    
    // Check if we should highlight app settings (for background listening) - legacy route support
    if (location.state?.highlightApp) {
      setActiveTab('app');
      
      if (location.state?.highlightBackgroundSettings) {
        setHighlightBackgroundSettings(true);
      }
      if (location.state?.highlightActiveHours) {
        setHighlightActiveHours(true);
      }
      
      // Clear the state to prevent re-highlighting on future visits
      window.history.replaceState({}, document.title);
    }
  }, [location, navigationState, initialTab]);

  const settingsTabs = [
    { id: 'account', label: 'Account Settings' },
    { id: 'llm', label: 'LLM Provider' },
    { id: 'voice', label: 'Mic & Speaker' },
    { id: 'privacy', label: 'Privacy & Security' },
    { id: 'app', label: 'App Settings' }
  ];

  const handleResetSettings = () => {
    // Reset all settings to defaults
    console.log('Resetting all settings to defaults...');
    
    // Reset localStorage settings to defaults
    localStorage.setItem('backgroundListening', 'true');
    localStorage.setItem('activeHours', 'false');
    localStorage.setItem('startTime', '09:00');
    localStorage.setItem('endTime', '18:00');
    localStorage.removeItem('selectedMicrophone');
    localStorage.removeItem('selectedSpeaker');
    
    // Trigger settings change events to update components
    window.dispatchEvent(new CustomEvent('settingsChanged', {
      detail: { 
        backgroundListening: true,
        activeHours: false,
        startTime: '09:00',
        endTime: '18:00'
      }
    }));
    
    // Trigger device reset events
    window.dispatchEvent(new CustomEvent('deviceReset'));
    
    setHasChanges(false);
    
    // Show confirmation or feedback to user
    console.log('Settings reset to defaults');
  };

  const renderSettingsPanel = () => {
    switch (activeTab) {
      case 'account':
        return <AccountSettings onSettingsChange={setHasChanges} />;
      case 'llm':
        return <LLMProviderSettings onSettingsChange={setHasChanges} />;
      case 'voice':
        return <VoiceSettings onSettingsChange={setHasChanges} highlightVoice={highlightVoice} />;
      case 'app':
        return <AppSettings 
          onSettingsChange={setHasChanges} 
          highlightBackgroundSettings={highlightBackgroundSettings}
          highlightActiveHours={highlightActiveHours}
        />;
      default:
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '200px',
            color: '#6b7280',
            fontSize: '0.875rem'
          }}>
            {settingsTabs.find(tab => tab.id === activeTab)?.label} - Coming Soon
          </div>
        );
    }
  };

  return (
    <SettingsContainer>
      <SettingsHeader>
        <HeaderLeft>
          <BackButton
            onClick={onBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft />
            Back
          </BackButton>
          <SettingsTitle>Settings</SettingsTitle>
        </HeaderLeft>

        <HeaderActions>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetSettings}
            disabled={!hasChanges}
          >
            <RotateCcw />
            Reset to Defaults
          </Button>
        </HeaderActions>
      </SettingsHeader>

      <SettingsContent>
        <SettingsNav>
          <NavList>
            {settingsTabs.map((tab) => (
              <NavItem key={tab.id}>
                <NavButton
                  isActive={activeTab === tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (tab.id === 'app') {
                      setHighlightVoice(false);
                    }
                  }}
                >
                  {tab.label}
                </NavButton>
              </NavItem>
            ))}
          </NavList>
        </SettingsNav>

        <SettingsPanel
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {renderSettingsPanel()}
        </SettingsPanel>
      </SettingsContent>
    </SettingsContainer>
  );
};

export default Settings;
